/**
 * Funções de formatação para Nova OS
 * Migrado de App.js para estrutura modular
 */

/**
 * Aplica máscara de telefone (XX)XXXXX-XXXX
 * @param {string} value - Valor a ser formatado
 * @returns {string}
 */
export const applyPhoneMask = (value) => {
  // Remove tudo que não é dígito
  const numbers = value.replace(/\D/g, '');
  
  // Aplica a máscara
  if (numbers.length <= 2) {
    return numbers;
  } else if (numbers.length <= 7) {
    return `(${numbers.slice(0, 2)})${numbers.slice(2)}`;
  } else {
    return `(${numbers.slice(0, 2)})${numbers.slice(2, 7)}-${numbers.slice(7, 11)}`;
  }
};

/**
 * Formata número com máscara de milhar (preservando sinal)
 * @param {number|string} num - Número a ser formatado
 * @returns {string}
 */
export const formatNumber = (num) => {
  if (num == null || num === '') return '0';
  const n = Number(num);
  if (isNaN(n)) return '0';
  const isNegative = n < 0;
  const absStr = Math.abs(n).toString();
  const formatted = absStr.replace(/\B(?=(\d{3})+(?!\d))/g, '.');
  return isNegative ? `-${formatted}` : formatted;
};

/**
 * Formata número com máscara de milhar (para contadores)
 * @param {number|string} value - Valor a ser formatado
 * @returns {string}
 */
export const formatNumberWithMask = (value) => {
  if (!value && value !== 0) return '';
  const numStr = String(value).replace(/[^0-9]/g, '');
  if (!numStr) return '';
  return numStr.replace(/\B(?=(\d{3})+(?!\d))/g, '.');
};

/**
 * Remove máscara de número
 * @param {string} value - Valor com máscara
 * @returns {string}
 */
export const removeMask = (value) => {
  return String(value).replace(/[^0-9]/g, '');
};

/**
 * Traduz tipo de preventiva para texto legível
 * @param {string} type - Código do tipo (E, B, A, R, D, I, S, N)
 * @returns {string}
 */
export const translatePreventivaType = (type) => {
  const types = {
    'E': 'ESTOQUE',
    'B': 'BALCÃO',
    'A': 'AFERIÇÃO',
    'R': 'RETORNO-RECARGA',
    'D': 'DESINSTALAÇÃO',
    'I': 'INSTALAÇÃO',
    'S': 'PREVENTIVA',
    'N': 'NORMAL'
  };
  return types[type] || type || 'N/A';
};

/**
 * Calcula diferença de tempo entre duas horas
 * @param {string} horaIni - Hora inicial (formato HH:mm ou HH:mm:ss)
 * @param {string} horaFim - Hora final (formato HH:mm ou HH:mm:ss)
 * @returns {string} - Diferença formatada (HH:mm)
 */
export const calculateTimeDifference = (horaIni, horaFim) => {
  if (!horaIni || !horaFim) return 'N/A';
  
  try {
    // Formato esperado: "HH:mm" ou "HH:mm:ss"
    const [hIni, mIni] = horaIni.split(':').map(Number);
    const [hFim, mFim] = horaFim.split(':').map(Number);
    
    const totalIni = hIni * 60 + mIni;
    const totalFim = hFim * 60 + mFim;
    
    let diff = totalFim - totalIni;
    
    // Se a diferença for negativa, assumir que passou meia-noite
    if (diff < 0) {
      diff += 24 * 60; // Adicionar 24 horas em minutos
    }
    
    const hours = Math.floor(diff / 60);
    const minutes = diff % 60;
    
    return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`;
  } catch (error) {
    return 'N/A';
  }
};

/**
 * Formata endereço usando dados do OpenStreetMap
 * @param {object} item - Item de endereço do Nominatim
 * @param {string} userQuery - Query original do usuário
 * @returns {string}
 */
export const formatAddress = (item, userQuery) => {
  const addr = item.address || {};
  
  // Extrair componentes do endereço
  const road = addr.road || addr.street || addr.pedestrian || '';
  const houseNumber = addr.house_number || addr.house_name || '';
  
  // Buscar bairro com prioridade: suburb > neighbourhood > quarter
  let suburb = '';
  if (addr.suburb) {
    suburb = addr.suburb;
  } else if (addr.neighbourhood) {
    suburb = addr.neighbourhood;
  } else if (addr.quarter) {
    suburb = addr.quarter;
  }
  // Filtrar campos que contenham termos administrativos indesejados
  if (suburb && (suburb.toLowerCase().includes('subdistrito') || 
                 suburb.toLowerCase().includes('distrito administrativo') ||
                 suburb.match(/^\d+º/))) {
    suburb = '';
  }
  
  const city = addr.city || addr.town || addr.village || addr.municipality || '';
  const postcode = addr.postcode || '';
  
  // Tentar extrair número do query do usuário se não estiver no endereço
  let extractedNumber = houseNumber;
  if (!extractedNumber && userQuery) {
    // Procurar por padrões comuns de número: "123", "123A", "S/N", etc.
    const numberPatterns = [
      /\b(\d+[A-Za-z]?)\b/,  // Número com possível letra (ex: 123A)
      /\b(S\/N|s\/n)\b/,     // Sem número
      /\b(\d+)\b/            // Apenas número
    ];
    
    for (const pattern of numberPatterns) {
      const match = userQuery.match(pattern);
      if (match) {
        extractedNumber = match[1];
        break;
      }
    }
  }
  
  // Montar endereço formatado
  const parts = [];
  if (road) {
    parts.push(road);
    if (extractedNumber) {
      parts[parts.length - 1] = `${road}, ${extractedNumber}`;
    }
  } else if (extractedNumber) {
    parts.push(extractedNumber);
  }
  if (suburb) parts.push(suburb);
  if (city) parts.push(city);
  if (postcode) parts.push(`CEP ${postcode}`);
  
  return parts.length > 0 ? parts.join(', ') : item.display_name;
};

/**
 * Formata data para formato PT-BR (DD/MM/YYYY)
 * @param {Date|string} date - Data a ser formatada
 * @returns {string}
 */
export const formatDateToPTBR = (date) => {
  if (!date) return '';
  
  let dateObj;
  if (typeof date === 'string') {
    // Se for string no formato YYYY-MM-DD
    if (date.match(/^\d{4}-\d{2}-\d{2}$/)) {
      const [year, month, day] = date.split('-');
      return `${day}/${month}/${year}`;
    }
    dateObj = new Date(date);
  } else {
    dateObj = date;
  }
  
  const day = String(dateObj.getDate()).padStart(2, '0');
  const month = String(dateObj.getMonth() + 1).padStart(2, '0');
  const year = dateObj.getFullYear();
  return `${day}/${month}/${year}`;
};

/**
 * Obtém data local como string (YYYY-MM-DD)
 * @param {Date} date - Data (opcional, usa hoje se não fornecido)
 * @returns {string}
 */
export const getLocalDateString = (date) => {
  if (!date) {
    const today = new Date();
    const year = today.getFullYear();
    const month = String(today.getMonth() + 1).padStart(2, '0');
    const day = String(today.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }
  
  // Se for string no formato YYYY-MM-DD, usar diretamente
  if (typeof date === 'string' && date.match(/^\d{4}-\d{2}-\d{2}$/)) {
    return date;
  }
  
  // Se for Date object, converter para string local sem timezone
  const d = new Date(date);
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

/**
 * Obtém data de hoje como string local (YYYY-MM-DD)
 * @returns {string}
 */
export const getTodayLocal = () => {
  const today = new Date();
  const year = today.getFullYear();
  const month = String(today.getMonth() + 1).padStart(2, '0');
  const day = String(today.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

/**
 * Calcula o próximo dia útil (pula fins de semana)
 * @param {Date} date - Data base (opcional, usa hoje se não fornecido)
 * @returns {Date}
 */
export const getNextBusinessDay = (date = new Date()) => {
  const currentDate = new Date(date);
  let nextDay = new Date(currentDate);
  nextDay.setDate(nextDay.getDate() + 1);
  const dayOfWeek = nextDay.getDay();
  if (dayOfWeek === 0) {
    nextDay.setDate(nextDay.getDate() + 1);
  } else if (dayOfWeek === 6) {
    nextDay.setDate(nextDay.getDate() + 2);
  }
  return nextDay;
};

/**
 * Calcula o dia após o próximo dia útil (para agendamento futuro)
 * @returns {Date}
 */
export const getDayAfterNextBusinessDay = () => {
  const nextBusinessDay = getNextBusinessDay();
  let dayAfter = new Date(nextBusinessDay);
  dayAfter.setDate(dayAfter.getDate() + 1);
  const dayOfWeek = dayAfter.getDay();
  if (dayOfWeek === 0) {
    dayAfter.setDate(dayAfter.getDate() + 1);
  } else if (dayOfWeek === 6) {
    dayAfter.setDate(dayAfter.getDate() + 2);
  }
  return dayAfter;
};

